import { QueuedIntakeFileInfo, UploadData } from '../../../store/uploadStore';
import { UploadNotRestoredError } from '../../../errors/UploadNotRestoredError';
import * as Sentry from '@sentry/browser';
import { executeUploadWithSentrySpan, setUploadAttributesOnSentrySpan } from './executeUploadWithSentrySpan';
import { registerFileInPilotApi } from './registerFileInPilotApi';
import { moveFileFromQueueToUpload } from './moveFileFromQueueToUpload';
import { sendUploadTimeSeconds, trackUploadStart } from './metrics/sendUploadTimeSeconds';
import { sendUploadCompletedPercent } from './metrics/sendUploadCompletedPercent';
import { uploadFileToStorage } from './uploadFileToStorage';
import getPilotApiService from '../../../api_services/PilotApiService';
import { resolveExifDate } from './resolveExifDate';

const PARALLEL_UPLOAD_LIMIT = 8;
let parallelUploadLimit = PARALLEL_UPLOAD_LIMIT;

/**
 * @TODO
 * 1. Start tracking retry errors
 * 2. If there are no retry errors in the last minute and the uploadStore.bytesPerSecond is very high, increase the max parallel limit and log it
 * For now we will have this easter-egg:
 */
declare global {
  interface Window {
    taSetParallelUploadLimit: (limit: number) => void;
  }
}
window.taSetParallelUploadLimit = (limit: number) => {
  if (Number.isInteger(limit) && limit >= 4 && limit <= 256) {
    parallelUploadLimit = limit;
  } else {
    alert('Must be an integer between 4 and 256');
  }
};

let currentUploadCount = 0;

export const uploadFile = (upload: UploadData, file: QueuedIntakeFileInfo, updateUpload: () => void) => {
  if (!file.validation || file.isBeingUploaded) {
    throw new Error('A file must be validated and not being uploaded to get to this point');
  }

  file.isBeingUploaded = true;
  updateUpload();
  currentUploadCount++;

  return executeUploadWithSentrySpan(upload, file, async (span: Sentry.Span) => {
    if (!upload.upload) throw new UploadNotRestoredError();

    const uploadStartTimeMs = Date.now();
    trackUploadStart(upload);

    try {
      await uploadFileToStorage(upload, file, uploadStartTimeMs, updateUpload);

      await registerFileInPilotApi(upload, file);

      setUploadAttributesOnSentrySpan(span, file.size!, uploadStartTimeMs);

      moveFileFromQueueToUpload(upload, file);

      if (upload.overview.photosCount.uploaded === upload.overview.photosCount.all) {
        // Self-service uploads keep their picked date: no schedule, no exifDate, nothing to move.
        const exifDate = upload.upload.flightScheduleId ? resolveExifDate(upload) : undefined;

        await getPilotApiService().markIntakeLoaded(
          upload.meta.abortController.signal,
          upload.upload.customer,
          upload.upload.site,
          upload.meta.type,
          upload.upload.date,
          upload.upload.flightScheduleId,
          exifDate,
        );

        if (exifDate && exifDate !== upload.upload.date) {
          upload.upload.date = exifDate;
          upload.overview.date = exifDate;
          updateUpload();
        }
      }
    } finally {
      sendUploadCompletedPercent(upload);
      if (upload.queue.length === 0) {
        sendUploadTimeSeconds(upload);
      }

      currentUploadCount--;
    }
  });
};

export const hasHitParallelUploadLimit = () => {
  return parallelUploadLimit - currentUploadCount <= 0;
};
